import {gql} from '@apollo/client';
import {entry as castEntry, objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React from 'react';

import {ArchivableItemREST, NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import * as ArchiveFilterUtil from 'archive/ArchiveFilterUtil';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {ArchiveOptionsForModal} from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import * as BenchlingUtils from 'BenchlingUtil';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {
  SchemasListingArchiveQuery,
  SchemasListingArchiveQueryVariables,
} from 'schemas/__generated/SchemasListingArchiveQuery';
import RelatedBenchlingAppsArchiveWarning from 'schemas/RelatedBenchlingAppsArchiveWarning';
import {schemaFieldsetNamePluralized} from 'schemas/schema-interface/util/schemaFieldsetName';
import {SCHEMA_TYPE_NAMES, SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';
import InlineBanner, {InlineBannerProps} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import EditableItemTable, {EditableItemRowProps} from 'ui-library/EditableItemTable';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import PlainSearchInput, {BorderRadius} from 'ui-library/PlainSearchInput';
import APIUtil from 'util/APIUtil';
import * as modals from 'util/Modals';
import {confirm} from 'util/Modals';
import strUtil from 'util/strUtil';
import {Optional} from 'util/Types';

const SCHEMA_COLUMN_HEADERS = [{name: 'Schemas', width: 12}];
const EMPTY_SEARCH_QUERY = '';
const NAME_FILTER_ID = 'nameFilterInput';

export type SchemasListingSchema = ArchivableItemREST & {id: string};

export const SCHEMAS_LISTING_ARCHIVE_QUERY: DocumentNode = gql`
  # schema.relatedBenclingApps does not query id
  # eslint-disable benchlint/id-field-required
  query SchemasListingArchiveQuery($id: StrictString!) @skipAudit @engComponent(name: SCHEMAS) {
    schema: schema(id: $id) {
      ... on SchemaInterface {
        id
        relatedBenchlingApps {
          name
          url
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface RowPermissions {
  isArchivable: boolean;
  isDeletable: boolean;
}

export interface SchemasListingProps<Schema extends SchemasListingSchema> {
  columnHeaders: Array<{name: string; width: number}>;
  customSearchControls?: JSX.Element;
  customFilterCallback?: (schemas: Array<Schema>) => Array<Schema>;
  errorMessage?: string;
  schemas?: Array<Schema> | null;
  schemaType: string;
  navigateToCreate?: () => void;
  urlForSchema: (schemaId: string) => string;
  archiveSchemas: (itemIds: Array<string>, purpose: string) => void;
  unarchiveSchemas: (itemIds: Array<string>) => void;
  deleteSchema?: (itemId: string) => void;
  isWritable: boolean;
  mapSchemaToRowValue: (schema: Schema) => Array<JSX.Element | string>;
  mapSchemaToRowPermissions?: (schema: Schema) => RowPermissions;
  getBannerInfo?: (filteredSchemas: Array<Schema>) => InlineBannerProps | null;
  customCreateButton?: JSX.Element;
}

interface State {
  selectedArchivePurposeFilters: Array<string>;
  searchQuery: string;
}

class SchemasListing<Schema extends SchemasListingSchema> extends React.Component<
  SchemasListingProps<Schema>,
  State
> {
  static defaultProps = {
    columnHeaders: SCHEMA_COLUMN_HEADERS,
    isWritable: true,
    mapSchemaToRowValue: (schema: SchemasListingSchema): [JSX.Element] => [<span>{schema.name}</span>],
  };

  constructor(props: SchemasListingProps<Schema>) {
    super(props);
    const initialQuery = BenchlingUtils.getQueryParam('q');
    this.state = {
      selectedArchivePurposeFilters: [NOT_ARCHIVED_PURPOSE_STRING],
      searchQuery: initialQuery || EMPTY_SEARCH_QUERY,
    };
  }

  private getSchemaUrlForRow = (itemIndex: number): string | null => {
    if (this.props.schemas != null) {
      const schema = this.props.schemas[itemIndex];
      return this.props.urlForSchema(schema.id);
    } else {
      return null;
    }
  };

  private getLoadedSchemas(): Array<Schema> {
    if (!this.props.schemas) {
      throw new Error('Expected schemas to be loaded');
    }
    return this.props.schemas;
  }

  private archiveSchema = (
    itemIds: Array<string>,
    purpose: string,
    schemaName: string,
    archiveWarning: JSX.Element | null
  ): void => {
    if (archiveWarning != null) {
      confirm(`Confirm archiving "${schemaName}"?`, [archiveWarning], ButtonStyle.NEGATIVE, {
        enableRedesign: true,
        acceptText: 'Archive',
        onSubmit: () => {
          this.props.archiveSchemas(itemIds, purpose);
        },
      });
    } else {
      this.props.archiveSchemas(itemIds, purpose);
    }
  };

  private openArchiveModal = (itemIndex: number): void => {
    const schema = this.getLoadedSchemas()[itemIndex];
    if (APIUtil.isSchemaInterface(schema.id)) {
      ArchiveModal.open({
        items: [schema],
        onArchive: (itemIds: Array<string>, purpose: string, options: ArchiveOptionsForModal) => {
          this.archiveSchema(itemIds, purpose, schema.name, null);
        },
      });
    } else {
      const promise = doQuerySafe<SchemasListingArchiveQuery, SchemasListingArchiveQueryVariables>(
        {query: SCHEMAS_LISTING_ARCHIVE_QUERY},
        {
          variables: {id: schema.id},
          fetchPolicy: 'network-only',
        }
      );
      ArchiveModal.open({
        items: [schema],
        onArchive: (itemIds: Array<string>, purpose: string, options: ArchiveOptionsForModal) =>
          promise
            .then((data) => {
              assert(!isInaccessible(data.schema), 'Expected schema to be accessible');
              const archiveWarning =
                data.schema.relatedBenchlingApps.length > 0 ? (
                  <RelatedBenchlingAppsArchiveWarning
                    itemName={schema.name}
                    itemType="schema"
                    relatedBenchlingApps={data.schema.relatedBenchlingApps}
                  />
                ) : null;
              this.archiveSchema(itemIds, purpose, schema.name, archiveWarning);
            })
            .catch(notifications.notifyError),
      });
    }
  };

  private createArchiveSchemaHandler = (): ((itemIndex: number) => void) | null =>
    this.props.schemas
      ? (itemIndex: number) => {
          this.openArchiveModal(itemIndex);
        }
      : null;

  private createUnarchiveSchemaHandler = (): ((itemIndex: number) => void) | null =>
    this.props.schemas
      ? (itemIndex: number) => {
          this.props.unarchiveSchemas([this.getLoadedSchemas()[itemIndex].id]);
        }
      : null;

  private createDeleteHandler = (): ((itemIndex: number) => void) | null =>
    this.props.schemas && this.props.deleteSchema
      ? (itemIndex: number) => {
          const schema = this.getLoadedSchemas()[itemIndex];
          modals
            .confirm(`Delete "${schema.name}"`, 'Are you sure you want to delete this schema?', 'danger')
            .done(() => {
              this.props.deleteSchema!(schema.id);
            });
        }
      : null;

  private handleChangeArchivePurposeFilters = (selectedArchivePurposeFilters: Array<string>): void => {
    this.setState({selectedArchivePurposeFilters});
  };

  private filterForArchivePurpose(schemas: Array<Schema>): Array<Schema> {
    return ArchiveFilterUtil.filterItemsToPurposes(
      schemas,
      this.state.selectedArchivePurposeFilters
    ) as Array<Schema>;
  }

  private getFilteredSchemas(schemas: Optional<Array<Schema>>): Array<Schema> {
    let filteredSchemas = this.filterForArchivePurpose(schemas || []);
    if (this.props.customFilterCallback) {
      filteredSchemas = this.props.customFilterCallback(filteredSchemas);
    }

    if (this.state.searchQuery !== EMPTY_SEARCH_QUERY) {
      filteredSchemas = filteredSchemas.filter((schema) =>
        strUtil.caseInsensitiveContains(schema.name, this.state.searchQuery)
      );
    }

    return filteredSchemas;
  }

  private getEmptyMessage(): string {
    if (this.props.errorMessage) {
      return this.props.errorMessage;
    } else if (this.props.schemaType === SCHEMA_TYPES.SCHEMA_INTERFACE) {
      return `No ${schemaFieldsetNamePluralized()} are present`;
    } else {
      return 'No schemas are present';
    }
  }

  private setSearchQuery = (searchQuery: string): void => {
    this.setState({
      searchQuery,
    });
  };

  override render(): JSX.Element {
    const schemaIndicesById = this.props.schemas
      ? // have to cast mapping function output because typescript treats [s.id, i] as ReactText[]
        objectFromEntries(this.props.schemas.map((s, i) => castEntry(s.id, i)))
      : {};
    const filteredSchemas = this.getFilteredSchemas(this.props.schemas);
    const rows: Array<EditableItemRowProps> | null = filteredSchemas
      ? filteredSchemas.map((schema) => {
          const row: EditableItemRowProps = {
            id: schema.id,
            itemIndex: schemaIndicesById[schema.id],
            isArchived: isArchived(schema),
            values: this.props.mapSchemaToRowValue(schema),
          };
          if (this.props.mapSchemaToRowPermissions) {
            const permissions = this.props.mapSchemaToRowPermissions(schema);
            row.isArchivable = permissions.isArchivable;
            row.isDeletable = permissions.isDeletable;
          }
          return row;
        })
      : null;
    const isLoading = this.props.schemas == null;
    const defaultSchemaSuffix = ' schemas';
    const schemaSuffix = this.props.schemaType === SCHEMA_TYPES.SCHEMA_INTERFACE ? 's' : defaultSchemaSuffix;
    const bannerInfo = this.props.getBannerInfo?.(filteredSchemas);
    return (
      <div className="settingsPage">
        <div className="settingsPage-contentWrapper">
          <EditableItemTable
            // @ts-expect-error
            tableName={`${SCHEMA_TYPE_NAMES[this.props.schemaType]}${schemaSuffix}`}
            columnHeaders={this.props.columnHeaders}
            emptyMessage={this.getEmptyMessage()}
            isLoading={isLoading}
            rows={rows}
            onCreate={
              this.props.navigateToCreate
                ? () => {
                    this.props.navigateToCreate!();
                  }
                : undefined
            }
            getEditUrl={this.getSchemaUrlForRow}
            onArchive={this.createArchiveSchemaHandler()}
            onUnarchive={this.createUnarchiveSchemaHandler()}
            onDelete={this.createDeleteHandler()}
            writable={this.props.isWritable}
            customCreateButton={this.props.customCreateButton}
            lowerSubheaderNode={bannerInfo != null ? <InlineBanner {...bannerInfo} /> : null}
            leftSubheaderNode={
              <div className={classNames('u-flex-display', css(styles.searchControls))}>
                <FormGroup>
                  <FieldLabel label="Filter by name" htmlFor={NAME_FILTER_ID} />
                  <PlainSearchInput
                    id={NAME_FILTER_ID}
                    className={css(styles.searchInput)}
                    borderRadius={BorderRadius.LARGE}
                    placeholder="Search"
                    value={this.state.searchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      this.setSearchQuery(e.target.value);
                    }}
                    onEscape={() => {
                      this.setSearchQuery(EMPTY_SEARCH_QUERY);
                    }}
                    onBlur={() => {
                      BenchlingUtils.setQueryParam('q', this.state.searchQuery || null);
                    }}
                  />
                </FormGroup>

                <ArchivePurposeDropdown
                  showLabel={true}
                  itemIds={isLoading ? [] : this.getLoadedSchemas().map((schema) => schema.id)}
                  onChangePurposes={this.handleChangeArchivePurposeFilters}
                  selectedPurposes={this.state.selectedArchivePurposeFilters}
                />
                {this.props.customSearchControls}
              </div>
            }
          />
        </div>
      </div>
    );
  }
}

const styles = StyleSheet.create({
  searchInput: {
    marginRight: 10,
    width: 200,
  },
  searchControls: {
    gap: '10px',
    marginRight: '10px',
  },
});

export default SchemasListing;
